/**
 * Rotate tool handler
 * - Click+drag: rotate selected object around its center
 * - Shows rotation angle overlay
 * - Shift: constrain to 15° increments
 * - Updates on release
 */

import { Vec2, angleBetween, radiansToDegrees } from '@/canvas/coordinates';
import { ToolHandler, ToolContext } from './tool-handler';
import { useEditorStore } from '@/core/stores/editor-store';
import { useStageStore } from '@/core/stores/stage-store';
import { useHistoryStore } from '@/core/stores/history-store';

interface RotateToolState {
  isRotating: boolean;
  objectCenter: Vec2 | null;
  startAngle: number | null;
  currentAngle: number | null;
}

const state: RotateToolState = {
  isRotating: false,
  objectCenter: null,
  startAngle: null,
  currentAngle: null,
};

const ROTATION_CONSTRAINT = 15; // degrees for shift key constraint

export function getRotateToolHandler(): ToolHandler {
  return {
    onMouseDown(ctx: ToolContext) {
      if (ctx.button !== 0) return; // Only left-click

      const { selectedObjectIds } = useEditorStore.getState();
      const { layout } = useStageStore.getState();

      if (selectedObjectIds.length === 0 || !layout) return;

      // For single object rotation, use that object's center
      // For multiple objects, use the first one
      const firstObjId = selectedObjectIds[0];
      const obj = layout.objects.find((o) => o.id === firstObjId);

      if (!obj) return;

      // Push pre-mutation snapshot BEFORE any rotation mutates the state
      const historyState = useHistoryStore.getState();
      historyState.pushState(layout);

      state.objectCenter = { x: obj.position.x, y: obj.position.y };
      state.isRotating = true;
      state.startAngle = radiansToDegrees(angleBetween(state.objectCenter, ctx.worldPos));
      state.currentAngle = obj.rotation.z || 0;
    },

    onMouseMove(ctx: ToolContext) {
      if (!state.isRotating || !state.objectCenter || state.startAngle === null || state.currentAngle === null) {
        return;
      }

      const { selectedObjectIds, snapToGrid: shouldSnap } = useEditorStore.getState();
      const stageState = useStageStore.getState();

      // Calculate current angle relative to center
      const currentAngle = radiansToDegrees(angleBetween(state.objectCenter, ctx.worldPos));
      let deltaAngle = currentAngle - state.startAngle;

      // Normalize to -180 to 180
      while (deltaAngle > 180) deltaAngle -= 360;
      while (deltaAngle < -180) deltaAngle += 360;

      let newRotation = state.currentAngle + deltaAngle;

      // Snap to 15 degree increments if shift is pressed
      if (shouldSnap) {
        newRotation = Math.round(newRotation / ROTATION_CONSTRAINT) * ROTATION_CONSTRAINT;
      }

      // Normalize to 0-360
      newRotation = ((newRotation % 360) + 360) % 360;

      // Update rotation for all selected objects
      for (const id of selectedObjectIds) {
        stageState.updateObject(id, {
          rotation: { x: 0, y: 0, z: newRotation },
        });
      }
    },

    onMouseUp() {
      if (!state.isRotating) return;

      // History was already pushed in onMouseDown (pre-rotation snapshot)
      useEditorStore.getState().setDirty(true);

      state.isRotating = false;
      state.objectCenter = null;
      state.startAngle = null;
      state.currentAngle = null;
    },

    getCursor() {
      return state.isRotating ? 'grabbing' : 'grab';
    },

    renderPreview(ctx2d: CanvasRenderingContext2D, _zoom: number) {
      if (!state.isRotating || !state.objectCenter || state.currentAngle === null) return;

      // Draw rotation center point
      ctx2d.fillStyle = 'rgba(255, 100, 100, 0.8)';
      ctx2d.beginPath();
      ctx2d.arc(state.objectCenter.x, state.objectCenter.y, 0.15, 0, Math.PI * 2);
      ctx2d.fill();

      // Draw angle text (if needed in actual canvas rendering)
      // This would be handled by the canvas component itself
    },
  };
}
